import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { IconChevronRight, IconMail, IconMapPin, IconPhone, IconPlus, IconUser, IconX } from "@tabler/icons-react";
import AttendanceDetailModal from "components/attendances/AttendanceDetailModal";
import AttendanceFormModal from "components/attendances/AttendanceFormModal";
import { useContractEditor } from "components/contracts/ContractEditorContext";
import { useCustomer360 } from "components/customer360/Customer360Context";
import useContractPermissions from "hooks/useContractPermissions";
import { formatCpf } from "utils/attendance";

const formatPhone = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length >= 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  }
  if (digits.length >= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6, 10)}`;
  }
  return digits;
};

const formatZip = (zipcode) => {
  const digits = String(zipcode || "").replace(/\D/g, "");
  if (digits.length !== 8) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const formatCardWhen = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const datePart = date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  const timePart = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return `${datePart} às ${timePart}`;
};

const attendancePillColors = (status, theme) => {
  if (status === "EM_ANDAMENTO") return { bgcolor: alpha(theme.palette.primary.main, 0.14), color: theme.palette.primary[800] };
  if (status === "A_INICIAR") return { bgcolor: theme.palette.warning.light, color: theme.palette.orange.dark };
  if (status === "CONCLUIDO") return { bgcolor: alpha(theme.palette.success.main, 0.2), color: theme.palette.success.dark };
  if (status === "CANCELADO") return { bgcolor: alpha(theme.palette.error.main, 0.12), color: theme.palette.error.dark };
  return { bgcolor: theme.palette.grey[100], color: theme.palette.text.secondary };
};

const formatDateBR = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = String(isoDate).split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}/${month}/${year}`;
};

const joinNonEmpty = (parts, separator) => parts.map((part) => String(part || "").trim()).filter(Boolean).join(separator);

const formatAddress = (customer) => {
  if (!customer) return "";
  const street = joinNonEmpty([customer.address_street, customer.address_number], ", ");
  const line1 = joinNonEmpty([street, customer.address_compl], ", ");
  const city = joinNonEmpty([customer.address_city, customer.address_state], " - ");
  const line2 = joinNonEmpty([customer.address_neighborhood, city], " · ");
  const zip = formatZip(customer.address_zipcode);
  const line3 = zip ? `CEP ${zip}` : "";
  return [line1, line2, line3].filter(Boolean).join("\n");
};

const CONTRACT_STATUS_LABELS = {
  AJUIZAR: "AJUIZAR",
  NAO_POSSUI_ABUSIVIDADE: "NÃO POSSUI ABUSIVIDADE",
  FALTAM_DOCUMENTOS: "FALTAM DOCUMENTOS",
  CANCELADO: "CANCELADO",
  PROTOCOLO_URGENTE: "PROTOCOLO URGENTE",
  PROTOCOLADO: "PROTOCOLADO",
  TRANSITO_EM_JULGADO: "TRÂNSITO EM JULGADO",
  REAJUIZAR: "REAJUIZAR",
  PASTA_FEITA: "PASTA FEITA",
  DESATUALIZADO: "DESATUALIZADO",
};

const contractStatusLabel = (status) => CONTRACT_STATUS_LABELS[status] || status || "—";

const contractStatusColor = (status) => {
  if (status === "CANCELADO") return "error";
  if (status === "PROTOCOLADO" || status === "TRANSITO_EM_JULGADO" || status === "PASTA_FEITA") return "success";
  if (status === "AJUIZAR" || status === "REAJUIZAR" || status === "PROTOCOLO_URGENTE") return "warning";
  if (status === "FALTAM_DOCUMENTOS" || status === "DESATUALIZADO") return "default";
  return "default";
};

const contractWhen = (contract) => formatDateBR(contract?.date) || formatDateBR(contract?.filed_at) || "";

const contractPillColors = (status, theme) => {
  const tone = contractStatusColor(status);
  if (tone === "error") return { bgcolor: alpha(theme.palette.error.main, 0.12), color: theme.palette.error.dark };
  if (tone === "success") return { bgcolor: alpha(theme.palette.success.main, 0.2), color: theme.palette.success.dark };
  if (tone === "warning") return { bgcolor: theme.palette.warning.light, color: theme.palette.orange.dark };
  return { bgcolor: theme.palette.grey[100], color: theme.palette.text.secondary };
};

const EmptyState = ({ message }) => (
  <Box sx={{ py: 3, textAlign: "center", border: "1px dashed", borderColor: "divider", borderRadius: 1 }}>
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

const StatusRail = ({ bgcolor, accent, label, value }) => (
  <Box
    sx={{
      width: 124,
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      px: 1.5,
      py: 1.25,
      bgcolor,
      borderLeft: "3px solid",
      borderLeftColor: accent,
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 700, color: "text.dark", lineHeight: 1.2, wordBreak: "break-word" }}>
      {value}
    </Typography>
    <Box sx={{ mt: 0.75, width: 28, height: 2, borderRadius: 1, bgcolor: accent }} />
  </Box>
);

const attendanceCardSx = {
  p: 0,
  borderRadius: "12px",
  border: "1px solid",
  borderColor: "divider",
  overflow: "hidden",
  alignItems: "stretch",
  "&:hover": {
    bgcolor: "grey.50",
    color: "inherit",
  },
};

const ContractCard = ({ contract, canEdit, contractEditorOpen, onOpen }) => {
  const theme = useTheme();
  const rail = contractPillColors(contract.status, theme);
  const number = contract.number || `#${contract.id}`;
  const process = contract.process_number || "";
  const category = contract.category || "";
  const meta = [contractWhen(contract), contract.lawyer?.name || ""].filter(Boolean).join(" · ");
  const title = category ? `Tipo ${category}` : (process ? `Processo ${process}` : "Contrato");

  return (
    <ListItemButton
      onClick={() => onOpen(contract)}
      disabled={canEdit && contractEditorOpen}
      disableRipple={!canEdit}
      aria-label={canEdit ? `Abrir contrato ${contract.number || contract.id}` : undefined}
      sx={{
        ...attendanceCardSx,
        cursor: canEdit ? "pointer" : "default",
        ...(!canEdit && { "&:hover": { bgcolor: "background.paper", color: "inherit" } }),
      }}
    >
      <StatusRail bgcolor={rail.bgcolor} accent={rail.color} label="Contrato" value={number} />
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.25 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, wordBreak: "break-word" }}>
            {title}
          </Typography>
          {meta && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {meta}
            </Typography>
          )}
          {process && category && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Processo {process}
            </Typography>
          )}
        </Box>
        <Chip
          label={contractStatusLabel(contract.status)}
          size="small"
          sx={(theme) => ({
            ...contractPillColors(contract.status, theme),
            flexShrink: 0,
            maxWidth: 148,
            height: "auto",
            fontWeight: 600,
            borderRadius: 999,
            "& .MuiChip-label": { px: 1.25, py: 0.5, whiteSpace: "normal", textAlign: "center", lineHeight: 1.2 },
          })}
        />
        {canEdit && (
          <Box sx={{ color: "text.secondary", display: "flex", flexShrink: 0 }}>
            <IconChevronRight stroke={1.5} size="1.1rem" />
          </Box>
        )}
      </Box>
    </ListItemButton>
  );
};

const ContractSummaryList = ({ contracts, canEdit, contractEditorOpen, onOpen }) => (
  <List dense disablePadding sx={{ "& > * + *": { mt: 1 } }}>
    {contracts.map((contract) => (
      <ContractCard
        key={contract.id}
        contract={contract}
        canEdit={canEdit}
        contractEditorOpen={contractEditorOpen}
        onOpen={onOpen}
      />
    ))}
  </List>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25, color: "text.secondary" }}>
    <Icon stroke={1.5} size="1rem" />
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}
    >
      {children}
    </Typography>
  </Box>
);

const InfoField = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word", whiteSpace: "pre-line" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const AttendanceCard = ({ attendance, onOpen }) => {
  const theme = useTheme();
  const rail = attendancePillColors(attendance.status, theme);
  const when = formatCardWhen(attendance.last_interaction_at || attendance.created_at);
  const who = attendance.last_interaction_user_name || attendance.created_by_name || "";
  const meta = [when, who].filter(Boolean).join(" · ");
  const process = attendance.process_number || attendance.contract_number || "";
  const subject = attendance.subject_display || attendance.subject || "Atendimento";
  const identifier = attendance.identifier || `#${attendance.id}`;

  return (
    <ListItemButton onClick={() => onOpen(attendance.id)} sx={attendanceCardSx}>
      <StatusRail bgcolor={rail.bgcolor} accent={rail.color} label="Atendimento" value={identifier} />
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.25 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, wordBreak: "break-word" }}>
            {subject}
          </Typography>
          {meta && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {meta}
            </Typography>
          )}
          {process && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Processo {process}
            </Typography>
          )}
        </Box>
        <Chip
          label={attendance.status_display || attendance.status || "—"}
          size="small"
          sx={(theme) => ({
            ...attendancePillColors(attendance.status, theme),
            flexShrink: 0,
            fontWeight: 600,
            height: 26,
            borderRadius: 999,
            "& .MuiChip-label": { px: 1.25 },
          })}
        />
        <Box sx={{ color: "text.secondary", display: "flex", flexShrink: 0 }}>
          <IconChevronRight stroke={1.5} size="1.1rem" />
        </Box>
      </Box>
    </ListItemButton>
  );
};

const ContactItem = ({ icon: Icon, children, sx }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, color: "text.secondary" }}>
    <Icon stroke={1.5} size="1rem" />
    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word", ...sx }}>
      {children}
    </Typography>
  </Box>
);

const Customer360Drawer = () => {
  const { customerId, open, closeCustomer } = useCustomer360();
  const { openNewContract, openContract, subscribeEditorOpen } = useContractEditor();
  const {
    canCreate,
    canEdit,
    canViewAttendances,
    canCreateAttendances,
    canAddAttendanceInteractions,
    loading: permissionsLoading,
  } = useContractPermissions();
  const [tab, setTab] = useState(0);
  const [customer, setCustomer] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [customerError, setCustomerError] = useState("");
  const [attendances, setAttendances] = useState([]);
  const [attendanceTotal, setAttendanceTotal] = useState(0);
  const [loadingAttendances, setLoadingAttendances] = useState(false);
  const [attendanceError, setAttendanceError] = useState("");
  const [attendancesForId, setAttendancesForId] = useState(null);
  const [selectedAttendanceId, setSelectedAttendanceId] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [contractTotal, setContractTotal] = useState(0);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [contractError, setContractError] = useState("");
  const [contractsForId, setContractsForId] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [contractEditorOpen, setContractEditorOpen] = useState(false);
  const customerIdRef = useRef(customerId);
  customerIdRef.current = customerId;
  const [seenCustomerId, setSeenCustomerId] = useState(customerId);

  if (seenCustomerId !== customerId) {
    setSeenCustomerId(customerId);
    setTab(0);
    setSelectedAttendanceId(null);
    setOpenCreate(false);
    setCustomer(null);
    setAttendances([]);
    setAttendanceTotal(0);
    setContracts([]);
    setContractTotal(0);
    setContractsForId(null);
    setAttendancesForId(null);
    setCustomerError("");
    setAttendanceError("");
    setContractError("");
    setLoadingCustomer(Boolean(customerId));
    setLoadingAttendances(false);
    setLoadingContracts(false);
  }

  const loadAttendances = useCallback(async (id, { cancelled } = {}) => {
    if (String(customerIdRef.current) !== String(id)) return;
    setLoadingAttendances(true);
    setAttendanceError("");
    try {
      const params = new URLSearchParams({
        customer_id: String(id),
        page_size: "100",
        ordering: "-last_interaction_at",
      });
      const response = await fetch(`/backend/attendances/?${params.toString()}`, {
        credentials: "include",
      });
      const payload = await response.json().catch(() => ({}));
      const stillCurrent = !cancelled?.() && String(customerIdRef.current) === String(id);
      if (!stillCurrent) return;
      if (!response.ok) {
        throw new Error(payload.detail || "Não foi possível carregar os atendimentos.");
      }
      setAttendances(payload.results || []);
      setAttendanceTotal(payload.count || 0);
      setAttendancesForId(id);
      return true;
    } catch (err) {
      if (cancelled?.() || String(customerIdRef.current) !== String(id)) return false;
      setAttendances([]);
      setAttendanceTotal(0);
      setAttendanceError(err.message);
      return false;
    } finally {
      if (!cancelled?.() && String(customerIdRef.current) === String(id)) {
        setLoadingAttendances(false);
      }
    }
  }, []);

  const loadContracts = useCallback(async (id, { cancelled } = {}) => {
    if (String(customerIdRef.current) !== String(id)) return false;
    setLoadingContracts(true);
    setContractError("");
    try {
      const params = new URLSearchParams({
        customer_id: String(id),
        page_size: "100",
      });
      const response = await fetch(`/backend/contracts/?${params.toString()}`, {
        credentials: "include",
      });
      const payload = await response.json().catch(() => ({}));
      const stillCurrent = !cancelled?.() && String(customerIdRef.current) === String(id);
      if (!stillCurrent) return false;
      if (!response.ok) {
        throw new Error(payload.detail || "Não foi possível carregar os contratos.");
      }
      const results = payload.results || [];
      results.sort((a, b) => String(b.date || b.filed_at || "").localeCompare(String(a.date || a.filed_at || "")));
      setContracts(results);
      setContractTotal(payload.count || 0);
      return true;
    } catch (err) {
      if (cancelled?.() || String(customerIdRef.current) !== String(id)) return false;
      setContracts([]);
      setContractTotal(0);
      setContractError(err.message);
      return false;
    } finally {
      if (!cancelled?.() && String(customerIdRef.current) === String(id)) {
        setLoadingContracts(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!customerId) return undefined;

    let cancelled = false;

    fetch(`/backend/customers/${customerId}/`, { credentials: "include" })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.detail || "Não foi possível carregar o cliente.");
        }
        return payload;
      })
      .then((payload) => {
        if (!cancelled && String(customerIdRef.current) === String(customerId)) setCustomer(payload);
      })
      .catch((err) => {
        if (!cancelled && String(customerIdRef.current) === String(customerId)) setCustomerError(err.message);
      })
      .finally(() => {
        if (!cancelled && String(customerIdRef.current) === String(customerId)) setLoadingCustomer(false);
      });

    return () => {
      cancelled = true;
    };
  }, [customerId]);

  useEffect(() => {
    if (!customerId || permissionsLoading || canViewAttendances) return;
    setLoadingAttendances(false);
    setAttendances([]);
    setAttendanceTotal(0);
    setAttendancesForId(null);
  }, [customerId, permissionsLoading, canViewAttendances]);

  useEffect(() => {
    if (tab !== 1 || !customerId || permissionsLoading || !canViewAttendances || attendancesForId === customerId) {
      return undefined;
    }
    let active = true;
    loadAttendances(customerId, { cancelled: () => !active });
    return () => {
      active = false;
    };
  }, [tab, customerId, permissionsLoading, canViewAttendances, attendancesForId, loadAttendances]);

  useEffect(() => {
    if (tab !== 2 || !customerId || contractsForId === customerId) return undefined;
    let active = true;
    loadContracts(customerId, { cancelled: () => !active }).then((loaded) => {
      if (active && loaded && String(customerIdRef.current) === String(customerId)) {
        setContractsForId(customerId);
      }
    });
    return () => {
      active = false;
    };
  }, [tab, customerId, contractsForId, loadContracts]);

  useEffect(() => subscribeEditorOpen(setContractEditorOpen), [subscribeEditorOpen]);

  const reloadContracts = useCallback(() => {
    const id = customerIdRef.current;
    if (!id) return undefined;
    return loadContracts(id).then((loaded) => {
      if (loaded && String(customerIdRef.current) === String(id)) setContractsForId(id);
    });
  }, [loadContracts]);

  const handleNewContract = () => {
    if (!customer?.id || contractEditorOpen) return;
    openNewContract({
      customerId: customer.id,
      customer: {
        id: customer.id,
        name: customer.name,
        cpf: customer.cpf,
      },
      lockCustomer: true,
      onSaved: () => reloadContracts(),
    });
  };

  const handleOpenContract = (contract) => {
    if (!contract?.id || contractEditorOpen || !canEdit) return;
    openContract(contract.id, {
      onSaved: () => reloadContracts(),
    });
  };

  useEffect(() => {
    if (!openCreate || subjects.length) return undefined;
    let active = true;
    fetch("/backend/attendances/subjects/", { credentials: "include" })
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => {
        if (active) setSubjects(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setSubjects([]);
      });
    return () => {
      active = false;
    };
  }, [openCreate, subjects.length]);

  const phone = formatPhone(customer?.phone);
  const email = customer?.mail || "";
  const cpf = customer?.cpf ? formatCpf(customer.cpf) : "";
  const showCpf = Boolean(cpf && cpf !== "—");
  const actionButtonSx = { flex: "1 1 160px" };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={closeCustomer}
        ModalProps={{
          disableEnforceFocus: Boolean(selectedAttendanceId || openCreate || contractEditorOpen),
          disableEscapeKeyDown: Boolean(selectedAttendanceId || openCreate || contractEditorOpen),
        }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 560 },
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box sx={{ px: 3, pt: 1.5, pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Typography component="h2" variant="h4" sx={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
              {customer?.name || (loadingCustomer ? "Carregando..." : "—")}
            </Typography>
            {customer?.id && (
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, pt: 0.35, whiteSpace: "nowrap" }}>
                Cliente {customer.id}
              </Typography>
            )}
            <IconButton aria-label="Fechar visão do cliente" onClick={closeCustomer} size="small" sx={{ mt: -0.25 }}>
              <IconX stroke={1.5} size="1.3rem" />
            </IconButton>
          </Box>
          {(showCpf || phone || email) && (
            <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
              {(showCpf || phone) && (
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 1.25, rowGap: 0.5 }}>
                  {showCpf && <ContactItem icon={IconUser}>CPF {cpf}</ContactItem>}
                  {showCpf && phone && (
                    <Box component="span" sx={{ width: "1px", height: 14, bgcolor: "divider", flexShrink: 0 }} />
                  )}
                  {phone && <ContactItem icon={IconPhone}>{phone}</ContactItem>}
                </Box>
              )}
              {email && <ContactItem icon={IconMail} sx={{ wordBreak: "break-all" }}>{email}</ContactItem>}
            </Box>
          )}
          {(canCreateAttendances || canCreate) && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
              {canCreateAttendances && (
                <Button
                  variant="outlined"
                  onClick={() => setOpenCreate(true)}
                  disabled={!customer?.id}
                  startIcon={<IconPlus stroke={1.5} size="1rem" />}
                  sx={actionButtonSx}
                >
                  Novo atendimento
                </Button>
              )}
              {canCreate && (
                <Button
                  variant="outlined"
                  onClick={handleNewContract}
                  disabled={!customer?.id || contractEditorOpen}
                  startIcon={<IconPlus stroke={1.5} size="1rem" />}
                  sx={actionButtonSx}
                >
                  Novo contrato / processo
                </Button>
              )}
            </Box>
          )}
        </Box>
        {loadingCustomer && <LinearProgress />}
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 1.5 }}>
          <Tabs
            value={tab}
            onChange={(event, next) => setTab(next)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              "& .MuiTab-root": { minHeight: 40, py: 0, px: 1.5 },
            }}
          >
            <Tab label="Visão Geral" />
            <Tab label="Atendimentos" />
            <Tab label="Contratos / Processos" />
          </Tabs>
        </Box>
        <Box sx={{ flex: 1, overflow: "auto", px: 3, pt: 1.75, pb: 2.5 }}>
          {tab === 0 && (
            <>
              {customerError && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {customerError}
                </Alert>
              )}
              {!customerError && !loadingCustomer && (
                <>
                  <Box>
                    <SectionTitle icon={IconUser}>Dados pessoais</SectionTitle>
                    <Grid container>
                      <Grid item xs={12} sm={6}>
                        <InfoField label="RG" value={customer?.rg} />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        sx={{
                          mt: { xs: 1.25, sm: 0 },
                          pl: { sm: 2 },
                          borderLeft: { sm: "1px solid" },
                          borderColor: "divider",
                        }}
                      >
                        <InfoField label="Profissão" value={customer?.profession?.name} />
                      </Grid>
                    </Grid>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <SectionTitle icon={IconMapPin}>Endereço</SectionTitle>
                    <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "pre-line" }}>
                      {formatAddress(customer) || "—"}
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}

          {tab === 1 && (
            <>
              {!permissionsLoading && !canViewAttendances && (
                <Alert severity="warning">Você não tem permissão para visualizar atendimentos.</Alert>
              )}
              {(permissionsLoading || loadingAttendances || (canViewAttendances && customerId && attendancesForId !== customerId)) && canViewAttendances && !attendanceError && (
                <LinearProgress sx={{ mb: 2 }} />
              )}
              {attendanceError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {attendanceError}
                </Alert>
              )}
              {attendancesForId === customerId && !loadingAttendances && !attendanceError && attendances.length === 0 && (
                <EmptyState message="Nenhum atendimento para este cliente." />
              )}
              {canViewAttendances && attendancesForId === customerId && attendances.length > 0 && (
                <Box>
                  {attendanceTotal > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {attendanceTotal} {attendanceTotal === 1 ? "atendimento" : "atendimentos"}
                    </Typography>
                  )}
                  <List dense disablePadding sx={{ "& > * + *": { mt: 1 } }}>
                    {attendances.map((attendance) => (
                      <AttendanceCard
                        key={attendance.id}
                        attendance={attendance}
                        onOpen={setSelectedAttendanceId}
                      />
                    ))}
                  </List>
                  {attendanceTotal > attendances.length && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                      Exibindo {attendances.length} de {attendanceTotal} atendimentos mais recentes.
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}

          {tab === 2 && (
            <>
              {(loadingContracts || (tab === 2 && customerId && contractsForId !== customerId)) && !contractError && (
                <LinearProgress sx={{ mb: 2 }} />
              )}
              {contractError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {contractError}
                </Alert>
              )}
              {contractsForId === customerId && !loadingContracts && !contractError && contracts.length === 0 && (
                <EmptyState message="Nenhum contrato/processo encontrado para este cliente." />
              )}
              {contractsForId === customerId && contracts.length > 0 && (
                <Box>
                  {contractTotal > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {contractTotal} {contractTotal === 1 ? "contrato/processo" : "contratos/processos"}
                    </Typography>
                  )}
                  <ContractSummaryList
                    contracts={contracts}
                    canEdit={canEdit}
                    contractEditorOpen={contractEditorOpen}
                    onOpen={handleOpenContract}
                  />
                  {contractTotal > contracts.length && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                      Exibindo {contracts.length} de {contractTotal} contratos.
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Drawer>

      <AttendanceDetailModal
        open={Boolean(selectedAttendanceId)}
        attendanceId={selectedAttendanceId}
        onClose={() => setSelectedAttendanceId(null)}
        canAddInteraction={canAddAttendanceInteractions}
        onAttendanceUpdated={() => {
          if (customerId && canViewAttendances) loadAttendances(customerId);
        }}
      />
      <AttendanceFormModal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onSaved={(payload) => {
          setOpenCreate(false);
          if (customerId && canViewAttendances) loadAttendances(customerId);
          if (payload?.id && canViewAttendances) setSelectedAttendanceId(payload.id);
        }}
        subjects={subjects}
        initialCustomer={customer?.id ? { id: customer.id, name: customer.name, cpf: customer.cpf } : null}
        lockCustomer
      />
    </>
  );
};

export default Customer360Drawer;
